export type DxNumKey =
  | "g"
  | "p"
  | "a"
  | "ah"
  | "uk_minggu"
  | "uk_hari"
  | "hari_ke"
  | "durasi";

export type DxTextKey = "penyerta" | "keterangan";
export type DxOptKey = "satuan";
export type DxKey = DxNumKey | DxTextKey | DxOptKey;

export type DxSeg =
  | { t: "lit"; text: string }
  | { t: "num"; key: DxNumKey; lead?: string; suffix?: string; label: string }
  | { t: "opt"; key: DxOptKey; options: string[]; label: string }
  | { t: "text"; key: DxTextKey; lead?: string; label: string };

export interface DxSpec {
  label: string;
  segments: DxSeg[];
}

export type DxValues = Partial<Record<DxKey, string | number | null>>;

export const FASE_LAINNYA = "lainnya";
export const FASE_LAINNYA_STORED = "dalam_persalinan";

const seg = {
  g: { t: "num", key: "g", lead: "G", label: "G (Gravida)" } as DxSeg,
  p: { t: "num", key: "p", lead: "P", label: "P (Para)" } as DxSeg,
  a: { t: "num", key: "a", lead: "A", label: "A (Abortus)" } as DxSeg,
  ah: { t: "num", key: "ah", lead: "AH", label: "AH (Anak Hidup)" } as DxSeg,
  ukMinggu: {
    t: "num",
    key: "uk_minggu",
    lead: "Hamil",
    suffix: "mg",
    label: "Usia Hamil (minggu)"
  } as DxSeg,
  ukHari: {
    t: "num",
    key: "uk_hari",
    suffix: "hari",
    label: "Usia Hamil (hari)"
  } as DxSeg,
  hariKe: {
    t: "num",
    key: "hari_ke",
    lead: "hari ke",
    label: "Hari ke-"
  } as DxSeg,
  penyerta: {
    t: "text",
    key: "penyerta",
    lead: "dengan",
    label: "Penyerta"
  } as DxSeg,
  keterangan: { t: "text", key: "keterangan", label: "Keterangan" } as DxSeg
};

const GPAAH: DxSeg[] = [seg.g, seg.p, seg.a, seg.ah];
const GAAH: DxSeg[] = [seg.g, seg.a, seg.ah];
const PAAH: DxSeg[] = [seg.p, seg.a, seg.ah];
const lit = (text: string): DxSeg => ({ t: "lit", text });

export const DIAGNOSA_SPEC: Record<string, DxSpec> = {
  kala1_laten: {
    label: "1. Kala I Fase Laten",
    segments: [
      ...GPAAH,
      seg.ukMinggu,
      seg.ukHari,
      lit("dalam persalinan kala I fase laten"),
      seg.penyerta
    ]
  },
  kala1_aktif: {
    label: "2. Kala I Fase Aktif",
    segments: [
      ...GPAAH,
      seg.ukMinggu,
      seg.ukHari,
      lit("dalam persalinan kala I fase aktif"),
      seg.penyerta
    ]
  },
  kala2: {
    label: "3. Kala II",
    segments: [
      ...GPAAH,
      seg.ukMinggu,
      lit("dalam persalinan kala II"),
      seg.keterangan
    ]
  },
  kala3: {
    label: "4. Kala III",
    segments: [...GAAH, seg.ukMinggu, lit("dalam persalinan kala III")]
  },
  kala4: {
    label: "5. Kala IV",
    segments: [...GAAH, seg.ukMinggu, lit("dalam persalinan kala IV")]
  },
  belum_dalam_persalinan: {
    label: "6. Belum Dalam Persalinan",
    segments: [
      ...GPAAH,
      seg.ukMinggu,
      seg.ukHari,
      lit("belum dalam persalinan"),
      seg.penyerta
    ]
  },
  post_sc: {
    label: "7. Post Sectio Caesaria",
    segments: [
      ...PAAH,
      lit("post sectio caesaria"),
      seg.hariKe,
      seg.penyerta
    ]
  },
  post_partum_pervaginam: {
    label: "8. Post Partum Pervaginam",
    segments: [
      ...PAAH,
      lit("post partum pervaginam"),
      seg.hariKe,
      seg.penyerta
    ]
  },
  post_vakum: {
    label: "9. Post Vakum Ekstraksi",
    segments: [
      ...PAAH,
      lit("post vakum ekstraksi"),
      seg.hariKe,
      seg.penyerta
    ]
  },
  gyn: {
    label: "10. Kasus Ginekologi",
    segments: [...PAAH, seg.keterangan]
  },
  abortus_iminen: {
    label: "11. Abortus Iminen",
    segments: [
      ...GPAAH,
      seg.ukMinggu,
      lit("dengan abortus iminen"),
      seg.penyerta
    ]
  },
  abortus_incomplete: {
    label: "12. Abortus Incomplete",
    segments: [
      ...GPAAH,
      seg.ukMinggu,
      lit("dengan abortus incomplete"),
      seg.penyerta
    ]
  },
  abortus_insipien: {
    label: "13. Abortus Insipien",
    segments: [...GPAAH, seg.ukMinggu, lit("abortus insipien")]
  },
  blighted_ovum: {
    label: "14. Blighted Ovum",
    segments: [...GPAAH, seg.ukMinggu, lit("blighted ovum")]
  },
  abortus_complete: {
    label: "15. Abortus Complete",
    segments: [
      ...GPAAH,
      seg.ukMinggu,
      lit("abortus complete"),
      seg.penyerta
    ]
  },
  hiperemesis: {
    label: "16. Hiperemesis Gravidarum",
    segments: [
      ...GPAAH,
      seg.ukMinggu,
      lit("hiperemesis gravidarum"),
      seg.penyerta
    ]
  },
  pprom: {
    label: "17. PPROM",
    segments: [
      ...GPAAH,
      seg.ukMinggu,
      lit("PPROM"),
      { t: "num", key: "durasi", label: "Lama Ketuban Pecah" },
      {
        t: "opt",
        key: "satuan",
        options: ["jam", "hari"],
        label: "Satuan"
      }
    ]
  },
  partus_prematurus_iminen: {
    label: "18. Partus Prematurus Iminen",
    segments: [
      ...GPAAH,
      seg.ukMinggu,
      lit("partus prematurus iminen"),
      seg.penyerta
    ]
  },
  bayi_cukup_bulan: {
    label: "19. Bayi Baru Lahir Cukup Bulan",
    segments: [lit("Bayi Baru Lahir Cukup Bulan"), seg.penyerta]
  },
  bayi_kurang_bulan: {
    label: "20. Bayi Baru Lahir Kurang Bulan",
    segments: [lit("Bayi Baru Lahir Kurang Bulan"), seg.penyerta]
  }
};

export function isPlaceholder(s: unknown): boolean {
  if (s === null || s === undefined) return true;
  return /^[.…\s/,-]*$/.test(String(s));
}

function valueOf(v: DxValues, key: DxKey): string {
  const raw = v[key];
  if (raw === null || raw === undefined) return "";
  const s = String(raw).trim();
  return isPlaceholder(s) ? "" : s;
}

export function composeDiagnosa(fase: string, v: DxValues): string {
  const spec = DIAGNOSA_SPEC[fase];
  if (!spec) return "";
  const out: string[] = [];
  spec.segments.forEach(s => {
    if (s.t === "lit") {
      out.push(s.text);
      return;
    }
    const val = valueOf(v, s.key);
    if (!val) return;
    if (s.t === "num" || s.t === "text") {
      if (s.lead) out.push(s.lead);
      out.push(val);
      if (s.t === "num" && s.suffix) out.push(s.suffix);
    } else {
      out.push(val);
    }
  });
  return out
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();
}

function esc(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&").replace(/\s+/g, "\\s+");
}

export function parseDiagnosa(fase: string, text: string): DxValues | null {
  const spec = DIAGNOSA_SPEC[fase];
  if (!spec || typeof text !== "string" || !text.trim()) return null;

  const keys: DxKey[] = [];
  let pattern = "^\\s*";
  spec.segments.forEach((s, idx) => {
    if (s.t === "lit") {
      pattern += esc(s.text) + "\\s*";
      return;
    }
    keys.push(s.key);
    if (s.t === "num") {
      pattern +=
        "(?:" +
        (s.lead ? esc(s.lead) + "\\s*" : "") +
        "(\\d+)" +
        (s.suffix ? "\\s*" + esc(s.suffix) : "") +
        "\\s*)?";
    } else if (s.t === "opt") {
      pattern += "(?:(" + s.options.map(esc).join("|") + ")\\s*)?";
    } else {
      const last = idx === spec.segments.length - 1;
      pattern +=
        "(?:" +
        (s.lead ? esc(s.lead) + "\\s*" : "") +
        (last ? "(.+?)" : "(.+?)") +
        "\\s*)?";
    }
  });
  pattern += "$";

  const m = text.match(new RegExp(pattern, "i"));
  if (!m) return null;

  const values: DxValues = {};
  keys.forEach((key, i) => {
    const raw = m[i + 1];
    values[key] = raw === undefined || isPlaceholder(raw) ? null : raw.trim();
  });
  return values;
}

export function parseDiagnosaLenient(text: string): DxValues {
  const values: DxValues = {};
  if (typeof text !== "string" || !text.trim()) return values;

  const head = text.slice(0, 60);
  const grab = (src: string, re: RegExp): string | null => {
    const m = src.match(re);
    return m && !isPlaceholder(m[1]) ? m[1].trim() : null;
  };

  values.ah = grab(head, /AH\s*(\d+)/i);
  values.g = grab(head, /G\s*(\d+)/i);
  values.p = grab(head, /P\s*(\d+)/i);
  values.a = grab(head, /A(?!H)\s*(\d+)/i);
  values.uk_minggu = grab(text, /Hamil\s*(\d+)\s*(?:mg|minggu)/i);
  values.uk_hari = grab(text, /(\d+)\s*hari(?!\s*ke)/i);
  values.hari_ke = grab(text, /hari\s*ke\s*(\d+)/i);
  values.penyerta = grab(text, /\bdengan\s+(.+)$/i);
  return values;
}

export function hasAnyValue(v: DxValues): boolean {
  return Object.keys(v).some(k => !!valueOf(v, k as DxKey));
}

export const ABORTUS_FASE = [
  "abortus_iminen",
  "abortus_incomplete",
  "abortus_insipien",
  "abortus_complete"
];

export function faseOptions(): Array<{ text: string; value: string }> {
  const opts = Object.keys(DIAGNOSA_SPEC).map(k => ({
    text: DIAGNOSA_SPEC[k].label,
    value: k
  }));
  opts.push({
    text: String(opts.length + 1) + ". Diagnosa lain (ketik bebas)",
    value: FASE_LAINNYA
  });
  return opts;
}
